import { useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useDropZone } from "../lib/useDropZone";
import { compressImage } from "../lib/compressImage";
import { AuthImage } from "./AuthImage";
import { Camera, Images, X, Loader2, Upload } from "lucide-react";

export function PhotoUploader({ photos, onChange, testid = "photo-uploader" }) {
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const handleFiles = async (files) => {
    setError("");
    const arr = Array.from(files).filter((f) => (f.type || "").startsWith("image/") || /\.(jpg|jpeg|png|webp|heic|gif)$/i.test(f.name));
    if (arr.length === 0) {
      setError("Solo immagini sono ammesse in questa sezione");
      return;
    }
    setUploading(true);
    try {
      let acc = photos;
      for (const file of arr) {
        const prepared = await compressImage(file);
        const fd = new FormData();
        fd.append("file", prepared);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        acc = [...acc, { id: data.id, filename: data.filename }];
        onChange(acc);
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Caricamento non riuscito");
    } finally {
      setUploading(false);
      if (cameraRef.current) cameraRef.current.value = "";
      if (galleryRef.current) galleryRef.current.value = "";
    }
  };

  const { dragProps, isDragOver } = useDropZone(handleFiles, { disabled: uploading });

  const remove = (id) => onChange(photos.filter((p) => p.id !== id));

  return (
    <div data-testid={testid} {...dragProps}
      className={`relative rounded-lg transition-all w-full min-w-0 ${isDragOver ? "ring-2 ring-brand bg-brand/5 p-2" : ""}`}>
      {isDragOver && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg bg-brand/10 z-10">
          <div className="flex items-center gap-2 text-brand font-bold text-sm bg-white/95 px-3 py-1.5 rounded-full border-2 border-brand shadow-md">
            <Upload className="w-4 h-4" /> Rilascia le foto
          </div>
        </div>
      )}
      {/* Iter122jg · Tile responsive: 20 px sotto sm, 24 px da sm in su → 4 fit su viewport mobile stretti (≥360 px) */}
      <div className="flex flex-wrap gap-2 sm:gap-3 w-full min-w-0">
        {photos.map((p) => (
          <div key={p.id} className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-lg overflow-hidden border-2 border-slate-200 shrink-0">
            <AuthImage fileId={p.id} className="w-full h-full object-cover" />
            <button type="button" data-testid={`remove-photo-${p.id}`} onClick={() => remove(p.id)}
              className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 hover:bg-red-700">
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
        <button type="button" data-testid="add-photo-button" onClick={() => cameraRef.current?.click()} disabled={uploading}
          className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-500 hover:border-[#C98AA0] hover:text-brand shrink-0">
          {uploading ? <Loader2 className="w-6 h-6 sm:w-7 sm:h-7 animate-spin" /> : <Camera className="w-6 h-6 sm:w-7 sm:h-7" />}
          <span className="text-[11px] sm:text-xs font-semibold mt-1 text-center px-1">{uploading ? "Carico..." : "Scatta foto"}</span>
        </button>
        <button type="button" data-testid="add-photo-gallery-button" onClick={() => galleryRef.current?.click()} disabled={uploading}
          className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-500 hover:border-[#C98AA0] hover:text-brand shrink-0">
          <Images className="w-6 h-6 sm:w-7 sm:h-7" />
          <span className="text-[11px] sm:text-xs font-semibold mt-1 text-center px-1">Dalla galleria</span>
        </button>
      </div>
      <input ref={cameraRef} type="file" accept="image/*" multiple capture="environment" className="hidden"
        onChange={(e) => e.target.files && handleFiles(e.target.files)} />
      <input ref={galleryRef} type="file" accept="image/*" multiple className="hidden"
        onChange={(e) => e.target.files && handleFiles(e.target.files)} />
      {error && <div className="mt-2 text-base text-red-700 font-semibold">{error}</div>}
    </div>
  );
}
